import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { toast } from "sonner";
import {
  BarChart3, Upload, RefreshCw, Sparkles, ExternalLink, Copy,
  TrendingUp, TrendingDown, Users2, Plane, Route as RouteIcon,
  AlertOctagon, Trophy, History, Download, X, Filter,
} from "lucide-react";
import { adminApi } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { inputCls } from "./shared";

const PKR = (n) =>
  new Intl.NumberFormat("en-PK", { maximumFractionDigits: 0 }).format(Math.round(n || 0));

const COHORT_STYLES = {
  loyalists: { bar: "border-l-emerald-500", pill: "bg-emerald-100 text-emerald-900" },
  one_time_bookers: { bar: "border-l-blue-500", pill: "bg-blue-100 text-blue-900" },
  high_intent_ghosts: { bar: "border-l-red-500", pill: "bg-red-100 text-red-900" },
  airline_blocked: { bar: "border-l-amber-500", pill: "bg-amber-100 text-amber-900" },
  sleepers: { bar: "border-l-slate-400", pill: "bg-slate-100 text-slate-700" },
  route_focused: { bar: "border-l-purple-500", pill: "bg-purple-100 text-purple-900" },
};

const FLAG_STYLE = {
  zero_conversion: "bg-red-100 text-red-900",
  underperforming: "bg-amber-100 text-amber-900",
  winner: "bg-emerald-100 text-emerald-900",
  normal: "bg-slate-100 text-slate-600",
};

function KpiCard({ label, value, sub, tone, testid }) {
  const toneCls =
    tone === "good" ? "text-emerald-700" :
    tone === "bad" ? "text-red-700" :
    tone === "warn" ? "text-amber-700" : "text-slate-900";
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-5 py-4" data-testid={testid}>
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`font-heading font-bold text-2xl mt-1 ${toneCls}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

export function B2BInsightsTab() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [airlines, setAirlines] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [users, setUsers] = useState({ users: [], cohorts: {} });
  const [uploads, setUploads] = useState([]);
  const [brief, setBrief] = useState(null);
  const [briefBusy, setBriefBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [nudgeTarget, setNudgeTarget] = useState(null);
  const [nudgeMsg, setNudgeMsg] = useState("");
  const [nudgeBusy, setNudgeBusy] = useState(false);
  const [facets, setFacets] = useState({ airlines: [], ods: [], statuses: [], min_date: null, max_date: null });
  const [filters, setFilters] = useState({ date_from: "", date_to: "", airline: "", od: "", status: "" });
  const [appliedFilters, setAppliedFilters] = useState({ date_from: "", date_to: "", airline: "", od: "", status: "" });
  const fileRef = useRef(null);

  const activeCount = useMemo(
    () => Object.values(appliedFilters).filter((v) => v && v !== "").length,
    [appliedFilters]
  );

  const filterParams = useMemo(() => {
    const p = {};
    if (appliedFilters.date_from) p.date_from = appliedFilters.date_from;
    if (appliedFilters.date_to) p.date_to = appliedFilters.date_to;
    if (appliedFilters.airline) p.airline = appliedFilters.airline;
    if (appliedFilters.od) p.od = appliedFilters.od;
    if (appliedFilters.status) p.status = appliedFilters.status;
    return p;
  }, [appliedFilters]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, al, rt, us, up, br, fc] = await Promise.all([
        adminApi().get("/admin/insights/overview", { params: filterParams }),
        adminApi().get("/admin/insights/airlines", { params: filterParams }),
        adminApi().get("/admin/insights/routes", { params: filterParams }),
        adminApi().get("/admin/insights/users", { params: filterParams }),
        adminApi().get("/admin/insights/uploads"),
        adminApi().get("/admin/insights/ai-summary/latest").catch(() => ({ data: null })),
        adminApi().get("/admin/insights/facets"),
      ]);
      setOverview(ov.data);
      setAirlines(al.data.airlines || []);
      setRoutes(rt.data.routes || []);
      setUsers(us.data);
      setUploads(up.data.uploads || []);
      setBrief(br.data && br.data.text ? br.data : null);
      setFacets(fc.data || facets);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load B2B insights");
    } finally {
      setLoading(false);
    }
  }, [filterParams]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const applyFilters = () => setAppliedFilters(filters);
  const resetFilters = () => {
    const empty = { date_from: "", date_to: "", airline: "", od: "", status: "" };
    setFilters(empty);
    setAppliedFilters(empty);
  };

  const downloadTemplate = async (fmt) => {
    try {
      const res = await adminApi().get(`/admin/insights/template?fmt=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `sellvago_b2b_bookings_template.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Template download failed");
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi().post("/admin/insights/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Added ${data.rows_added} • Skipped ${data.rows_skipped_duplicates} duplicates`);
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const generateBrief = async () => {
    setBriefBusy(true);
    try {
      const { data } = await adminApi().post("/admin/insights/ai-summary", null, { params: filterParams });
      setBrief(data);
      toast.success("Brief generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Brief generation failed");
    } finally {
      setBriefBusy(false);
    }
  };

  const openNudge = async (member, cohortId) => {
    setNudgeTarget({ member, cohortId });
    setNudgeMsg("");
    setNudgeBusy(true);
    try {
      const { data } = await adminApi().post("/admin/insights/message", {
        email: member.email, cohort: cohortId,
      });
      setNudgeMsg(data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Message generation failed");
    } finally {
      setNudgeBusy(false);
    }
  };

  const copyMsg = () => {
    navigator.clipboard.writeText(nudgeMsg);
    toast.success("Copied");
  };

  const waLink = () => {
    if (!nudgeMsg || !nudgeTarget) return null;
    // Log outreach + open wa.me — since we only have email, use wa.me/?text= (agent picks the contact)
    return `https://wa.me/?text=${encodeURIComponent(nudgeMsg)}`;
  };

  const onWaOpen = async () => {
    if (!nudgeTarget) return;
    try {
      await adminApi().post("/admin/insights/outreach", {
        email: nudgeTarget.member.email,
        cohort: nudgeTarget.cohortId,
        channel: "whatsapp",
        message_preview: nudgeMsg.slice(0, 280),
      });
    } catch { /* non-blocking */ }
  };

  const empty = overview && overview.empty;

  return (
    <div className="space-y-6" data-testid="b2b-insights-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-heading font-bold text-slate-900 text-lg">B2B Insights</p>
          <p className="text-xs text-slate-500 mt-1">
            Real bookings from agent portal. Cohorts recomputed live. Internal emails
            (<span className="font-mono">@amadeus.com, @sellvago.com</span>) auto-excluded from outreach.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative group">
            <button
              data-testid="insights-template-btn"
              className="inline-flex items-center gap-1.5 border border-slate-200 hover:border-slate-400 text-slate-700 text-xs font-bold px-3 py-2 rounded-md transition-colors duration-200"
            >
              <Download className="w-3.5 h-3.5" /> Template
            </button>
            <div className="absolute right-0 mt-1 min-w-[140px] bg-white border border-slate-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-150 z-20">
              <button
                data-testid="insights-template-xlsx"
                onClick={() => downloadTemplate("xlsx")}
                className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Download .xlsx
              </button>
              <button
                data-testid="insights-template-csv"
                onClick={() => downloadTemplate("csv")}
                className="w-full text-left px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 border-t border-slate-100"
              >
                Download .csv
              </button>
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFile}
            className="hidden"
            data-testid="insights-upload-input"
          />
          <button
            data-testid="insights-upload-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploadBusy}
            className="inline-flex items-center gap-1.5 bg-[#970C10] hover:bg-[#7A0A0D] disabled:opacity-40 text-white text-xs font-bold px-3 py-2 rounded-md transition-colors duration-200"
          >
            <Upload className="w-3.5 h-3.5" />
            {uploadBusy ? "Uploading…" : "Upload XLSX/CSV"}
          </button>
          <button
            data-testid="insights-refresh-btn"
            onClick={loadAll}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-[#970C10] px-2 py-2 transition-colors duration-200"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Filter bar */}
      {!loading && overview && !empty && (
        <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid="insights-filter-bar">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex items-center gap-1.5 text-xs font-bold text-slate-600 pr-2">
              <Filter className="w-3.5 h-3.5" />
              Filters
              {activeCount > 0 && (
                <span className="bg-[#970C10] text-white rounded-full text-[10px] px-1.5 py-px" data-testid="insights-filter-active-count">{activeCount}</span>
              )}
            </div>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">From</span>
              <input
                type="date"
                data-testid="insights-filter-from"
                value={filters.date_from}
                min={facets.min_date ? facets.min_date.slice(0, 10) : undefined}
                max={facets.max_date ? facets.max_date.slice(0, 10) : undefined}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                className="border border-slate-200 rounded-md px-2 py-1.5 text-xs"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">To</span>
              <input
                type="date"
                data-testid="insights-filter-to"
                value={filters.date_to}
                min={facets.min_date ? facets.min_date.slice(0, 10) : undefined}
                max={facets.max_date ? facets.max_date.slice(0, 10) : undefined}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                className="border border-slate-200 rounded-md px-2 py-1.5 text-xs"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Airline</span>
              <select
                data-testid="insights-filter-airline"
                value={filters.airline}
                onChange={(e) => setFilters({ ...filters, airline: e.target.value })}
                className="border border-slate-200 rounded-md px-2 py-1.5 text-xs min-w-[110px]"
              >
                <option value="">All airlines</option>
                {facets.airlines.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Route</span>
              <select
                data-testid="insights-filter-od"
                value={filters.od}
                onChange={(e) => setFilters({ ...filters, od: e.target.value })}
                className="border border-slate-200 rounded-md px-2 py-1.5 text-xs min-w-[130px]"
              >
                <option value="">All routes</option>
                {facets.ods.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Status</span>
              <select
                data-testid="insights-filter-status"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="border border-slate-200 rounded-md px-2 py-1.5 text-xs min-w-[110px]"
              >
                <option value="">Any status</option>
                {facets.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <div className="flex items-center gap-1.5 ml-auto">
              <button
                data-testid="insights-filter-apply"
                onClick={applyFilters}
                className="bg-slate-900 hover:bg-black text-white text-xs font-bold px-3 py-1.5 rounded-md transition-colors duration-200"
              >
                Apply
              </button>
              {activeCount > 0 && (
                <button
                  data-testid="insights-filter-reset"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-red-600 px-2 py-1.5 transition-colors duration-200"
                >
                  <X className="w-3.5 h-3.5" /> Reset
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {loading && <p className="text-sm text-slate-400 py-16 text-center">Loading insights…</p>}

      {!loading && empty && (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-10 text-center">
          <BarChart3 className="w-8 h-8 text-slate-300 mx-auto" />
          <p className="mt-3 text-sm font-bold text-slate-700">No B2B booking data yet</p>
          <p className="text-xs text-slate-500 mt-1">
            Upload an XLSX/CSV export from the agent portal. Required columns:
            <span className="font-mono ml-1">ROUTES, Airline Code, Booking Date, Lead Email Address, Total Amount, Booking Status</span>.
          </p>
          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              onClick={() => downloadTemplate("xlsx")}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#970C10] hover:text-[#7A0A0D]"
              data-testid="insights-empty-template"
            >
              <Download className="w-3.5 h-3.5" /> Download blank template
            </button>
          </div>
        </div>
      )}

      {!loading && overview && !empty && (
        <>
          {/* Tabs */}
          <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1 max-w-full overflow-x-auto no-scrollbar">
            {[
              { id: "overview", label: "Overview", icon: BarChart3 },
              { id: "airlines", label: "Airlines", icon: Plane },
              { id: "routes", label: "Routes", icon: RouteIcon },
              { id: "cohorts", label: "Cohorts", icon: Users2 },
              { id: "ai", label: "AI Brief", icon: Sparkles },
              { id: "uploads", label: "Uploads", icon: History },
            ].map((t) => (
              <button
                key={t.id}
                data-testid={`insights-subtab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-colors duration-200 ${
                  tab === t.id ? "bg-[#970C10] text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {tab === "overview" && (
            <div className="space-y-6" data-testid="insights-overview-pane">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard label="Total records" value={overview.total_records} testid="kpi-records" />
                <KpiCard label="Unique agents" value={overview.unique_agents} testid="kpi-agents" />
                <KpiCard
                  label="Confirmation rate"
                  value={`${overview.confirmation_rate}%`}
                  tone={overview.confirmation_rate < 20 ? "bad" : "good"}
                  testid="kpi-confrate"
                />
                <KpiCard
                  label="Abandon rate"
                  value={`${overview.abandon_rate}%`}
                  tone={overview.abandon_rate > 40 ? "bad" : "warn"}
                  testid="kpi-abandonrate"
                />
                <KpiCard label="Confirmed GMV" value={`PKR ${PKR(overview.confirmed_gmv)}`} tone="good" testid="kpi-gmv-conf" />
                <KpiCard
                  label="Abandoned GMV (recovery pot)"
                  value={`PKR ${PKR(overview.abandoned_gmv)}`}
                  tone="bad"
                  sub="Focus of retargeting"
                  testid="kpi-gmv-abandoned"
                />
                <KpiCard label="Cancelled GMV" value={`PKR ${PKR(overview.cancelled_gmv)}`} tone="warn" testid="kpi-gmv-cancel" />
                <KpiCard
                  label="Date range"
                  value={
                    overview.date_range?.from
                      ? `${new Date(overview.date_range.from).toLocaleDateString()} → ${new Date(overview.date_range.to).toLocaleDateString()}`
                      : "—"
                  }
                  testid="kpi-daterange"
                />
              </div>

              <div className="bg-white border border-slate-200 rounded-lg p-5">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Cohort snapshot</p>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(users.cohorts || {}).map(([id, c]) => (
                    <button
                      key={id}
                      data-testid={`cohort-snap-${id}`}
                      onClick={() => setTab("cohorts")}
                      className={`text-left border-l-4 ${COHORT_STYLES[id]?.bar || "border-l-slate-300"} bg-slate-50 hover:bg-slate-100 rounded-md px-3 py-2 transition-colors duration-200`}
                    >
                      <p className="text-xs font-bold text-slate-800">{c.meta.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{c.members.length} agents</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "airlines" && (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="insights-airlines-pane">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2">Airline</th>
                    <th className="text-right px-3 py-2">Searches</th>
                    <th className="text-right px-3 py-2">Confirmed</th>
                    <th className="text-right px-3 py-2">Abandoned</th>
                    <th className="text-right px-3 py-2">Conv %</th>
                    <th className="text-right px-3 py-2">GMV Lost (PKR)</th>
                    <th className="text-left px-3 py-2">Flag</th>
                  </tr>
                </thead>
                <tbody>
                  {airlines.map((a) => (
                    <tr key={a.airline} className="border-t border-slate-100" data-testid={`airline-row-${a.airline}`}>
                      <td className="px-4 py-2 font-mono font-bold text-slate-900">{a.airline}</td>
                      <td className="px-3 py-2 text-right text-slate-700">{a.searches}</td>
                      <td className="px-3 py-2 text-right text-emerald-700 font-semibold">{a.confirmed}</td>
                      <td className="px-3 py-2 text-right text-red-700">{a.abandoned}</td>
                      <td className="px-3 py-2 text-right font-bold">{a.conv_rate}%</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-600">{PKR(a.gmv_abandoned)}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${FLAG_STYLE[a.flag]}`}>
                          {a.flag === "winner" && <Trophy className="w-3 h-3" />}
                          {a.flag === "zero_conversion" && <AlertOctagon className="w-3 h-3" />}
                          {a.flag === "underperforming" && <TrendingDown className="w-3 h-3" />}
                          {a.flag === "normal" && <TrendingUp className="w-3 h-3" />}
                          {a.flag.replace("_", " ")}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "routes" && (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="insights-routes-pane">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2">Route</th>
                    <th className="text-right px-3 py-2">Searches</th>
                    <th className="text-right px-3 py-2">Confirmed</th>
                    <th className="text-right px-3 py-2">Abandoned</th>
                    <th className="text-right px-3 py-2">Conv %</th>
                    <th className="text-right px-3 py-2">Avg ticket</th>
                    <th className="text-right px-3 py-2">GMV Lost</th>
                  </tr>
                </thead>
                <tbody>
                  {routes.map((r) => (
                    <tr key={r.od} className="border-t border-slate-100" data-testid={`route-row-${r.od}`}>
                      <td className="px-4 py-2 font-mono font-bold text-slate-900">{r.od}</td>
                      <td className="px-3 py-2 text-right">{r.searches}</td>
                      <td className="px-3 py-2 text-right text-emerald-700 font-semibold">{r.confirmed}</td>
                      <td className="px-3 py-2 text-right text-red-700">{r.abandoned}</td>
                      <td className="px-3 py-2 text-right font-bold">{r.conv_rate}%</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-600">{PKR(r.avg_ticket)}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-600">{PKR(r.gmv_abandoned)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "cohorts" && (
            <div className="space-y-4" data-testid="insights-cohorts-pane">
              {Object.entries(users.cohorts || {}).map(([id, c]) => (
                <div
                  key={id}
                  className={`bg-white border border-slate-200 border-l-4 ${COHORT_STYLES[id]?.bar || "border-l-slate-300"} rounded-lg overflow-hidden`}
                  data-testid={`cohort-${id}`}
                >
                  <div className="px-5 py-3 flex items-center justify-between">
                    <div>
                      <p className="font-heading font-bold text-slate-900">{c.meta.label}</p>
                      <p className="text-xs text-slate-500">{c.meta.description}</p>
                    </div>
                    <p className="text-2xl font-bold text-slate-900" data-testid={`cohort-count-${id}`}>{c.members.length}</p>
                  </div>
                  {c.members.length > 0 ? (
                    <table className="w-full text-sm border-t border-slate-100">
                      <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wide">
                        <tr>
                          <th className="text-left px-5 py-2">Agent</th>
                          <th className="text-right px-3 py-2">Searches / Booked / Abandoned</th>
                          <th className="text-right px-3 py-2">GMV Lost</th>
                          <th className="text-left px-3 py-2">Top abandon</th>
                          <th className="text-right px-3 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.members.slice(0, 15).map((m) => (
                          <tr key={m.email} className="border-t border-slate-50" data-testid={`cohort-member-${id}-${m.email}`}>
                            <td className="px-5 py-2">
                              <p className="font-semibold text-slate-900">{m.user_name || m.email.split("@")[0]}</p>
                              <p className="text-xs text-slate-500 font-mono">{m.email}</p>
                            </td>
                            <td className="px-3 py-2 text-right text-xs font-mono text-slate-600">
                              {m.searches} / <span className="text-emerald-700">{m.confirmed}</span> / <span className="text-red-700">{m.abandoned}</span>
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-xs text-slate-600">{PKR(m.gmv_lost)}</td>
                            <td className="px-3 py-2 text-xs text-slate-500">
                              {m.top_airline_abandon && (
                                <span className="font-mono">{m.top_airline_abandon} · {m.top_od_abandon}</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <button
                                data-testid={`cohort-nudge-${id}-${m.email}`}
                                onClick={() => openNudge(m, id)}
                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#970C10] hover:text-[#7A0A0D] transition-colors duration-200"
                              >
                                <Sparkles className="w-3.5 h-3.5" /> Nudge
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="px-5 pb-4 text-xs text-slate-400">No agents in this cohort right now.</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === "ai" && (
            <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4" data-testid="insights-ai-pane">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-heading font-bold text-slate-900">AI executive brief</p>
                  <p className="text-xs text-slate-500 mt-0.5">Powered by Claude Sonnet 5.</p>
                </div>
                <button
                  data-testid="insights-ai-generate"
                  onClick={generateBrief}
                  disabled={briefBusy}
                  className="inline-flex items-center gap-1.5 bg-[#970C10] hover:bg-[#7A0A0D] disabled:opacity-40 text-white text-xs font-bold px-3 py-2 rounded-md transition-colors duration-200"
                >
                  <Sparkles className="w-3.5 h-3.5" /> {briefBusy ? "Analysing…" : (brief ? "Regenerate" : "Generate brief")}
                </button>
              </div>
              {brief ? (
                <>
                  <p className="text-[10px] text-slate-400">
                    Generated {new Date(brief.generated_at).toLocaleString()}
                  </p>
                  <pre className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800 bg-slate-50 rounded-md p-4 border border-slate-100" data-testid="insights-ai-brief-text">{brief.text}</pre>
                </>
              ) : (
                <p className="text-sm text-slate-400 py-8 text-center">
                  No brief yet. Click <b>Generate brief</b> — Claude will read the current airline/route/cohort data and return a tight action-oriented summary.
                </p>
              )}
            </div>
          )}

          {tab === "uploads" && (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="insights-uploads-pane">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-2">File</th>
                    <th className="text-right px-3 py-2">Total</th>
                    <th className="text-right px-3 py-2">Added</th>
                    <th className="text-right px-3 py-2">Dupes skipped</th>
                    <th className="text-right px-3 py-2">Errors</th>
                    <th className="text-right px-3 py-2">Uploaded</th>
                  </tr>
                </thead>
                <tbody>
                  {uploads.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400 text-xs">No uploads yet.</td></tr>
                  )}
                  {uploads.map((u) => (
                    <tr key={u.id} className="border-t border-slate-100" data-testid={`upload-row-${u.id}`}>
                      <td className="px-4 py-2 font-mono text-xs text-slate-700">{u.filename}</td>
                      <td className="px-3 py-2 text-right">{u.rows_total}</td>
                      <td className="px-3 py-2 text-right text-emerald-700 font-semibold">{u.rows_added}</td>
                      <td className="px-3 py-2 text-right text-slate-500">{u.rows_skipped_duplicates}</td>
                      <td className="px-3 py-2 text-right text-red-600">{u.rows_errors}</td>
                      <td className="px-3 py-2 text-right text-xs text-slate-500">{new Date(u.uploaded_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Nudge dialog */}
      <Dialog open={!!nudgeTarget} onOpenChange={(v) => !v && setNudgeTarget(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="nudge-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Nudge for {nudgeTarget?.member?.user_name || nudgeTarget?.member?.email}
            </DialogTitle>
            <DialogDescription className="text-xs">
              AI-drafted WhatsApp message tailored to this cohort. Review before sending.
            </DialogDescription>
          </DialogHeader>
          {nudgeTarget && (
            <div className="space-y-3">
              <div className="text-xs text-slate-500 flex flex-wrap gap-2">
                <span className={`px-2 py-0.5 rounded ${COHORT_STYLES[nudgeTarget.cohortId]?.pill || "bg-slate-100"}`}>
                  {users.cohorts?.[nudgeTarget.cohortId]?.meta?.label || nudgeTarget.cohortId}
                </span>
                <span className="font-mono">{nudgeTarget.member.email}</span>
                {nudgeTarget.member.top_od_abandon && (
                  <span className="font-mono bg-slate-100 px-1.5 rounded">{nudgeTarget.member.top_od_abandon}</span>
                )}
              </div>
              {nudgeBusy ? (
                <p className="text-sm text-slate-400 py-16 text-center">Writing the message…</p>
              ) : (
                <textarea
                  readOnly
                  className={`${inputCls} h-44 text-sm leading-relaxed`}
                  value={nudgeMsg}
                  data-testid="nudge-message-text"
                />
              )}
              <div className="flex gap-2">
                <button
                  data-testid="nudge-copy-btn"
                  onClick={copyMsg}
                  disabled={!nudgeMsg || nudgeBusy}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#970C10] hover:bg-[#7A0A0D] disabled:opacity-40 text-white font-bold text-sm py-2.5 rounded-lg transition-colors duration-200"
                >
                  <Copy className="w-4 h-4" /> Copy
                </button>
                {waLink() && (
                  <a
                    data-testid="nudge-wa-btn"
                    href={waLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={onWaOpen}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white font-bold text-sm py-2.5 rounded-lg transition-colors duration-200"
                  >
                    <ExternalLink className="w-4 h-4" /> Open WhatsApp
                  </a>
                )}
              </div>
              <p className="text-[10px] text-slate-400">
                WhatsApp opens a share dialog — pick the agent&apos;s number in your contact list. Opens are logged for attribution.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
